import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Landmark, Briefcase, ArrowRight } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/format";
import api from "@/lib/api";

export default function FundPositions() {
  const navigate = useNavigate();
  const [positions, setPositions] = useState(null);

  useEffect(() => {
    api.get("/portal/fund-positions").then(({ data }) => setPositions(data)).catch(() => setPositions([]));
  }, []);

  return (
    <AppShell title="Fund Positions" subtitle="Your LP commitments, capital account and distributions across funds and SPVs.">
      {positions && positions.length === 0 && (
        <Card className="flex flex-col items-center gap-3 rounded-xl border-dashed p-10 text-center" data-testid="lp-positions-empty">
          <Briefcase className="h-8 w-8 text-muted-foreground" />
          <p className="max-w-md text-sm text-muted-foreground">No fund positions yet. When a fund manager adds you as an LP with this email, your commitments and capital account will appear here.</p>
        </Card>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {(positions || []).map((p) => (
          <Card key={p.lp_id} className="cursor-pointer rounded-xl border p-5 transition-shadow hover:shadow-md" onClick={() => navigate(`/portal/funds/${p.lp_id}`)} data-testid={`lp-position-${p.lp_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Landmark className="h-5 w-5" /></div>
                <div>
                  <div className="font-semibold text-foreground">{p.fund_name}</div>
                  <div className="text-xs text-muted-foreground">{p.gp_name}{p.vintage_year ? ` · Vintage ${p.vintage_year}` : ""} · <span className="uppercase">{p.fund_type}</span></div>
                </div>
              </div>
              {p.open_calls > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700" data-testid={`lp-open-calls-${p.lp_id}`}>{p.open_calls} open call{p.open_calls === 1 ? "" : "s"}</span>}
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2 text-center">
              {[["Commitment", formatCurrency(p.commitment)], ["Paid-in", formatCurrency(p.contributed)], ["Distributed", formatCurrency(p.distributed)], ["NAV share", formatCurrency(p.nav_share)]].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-secondary/60 p-2">
                  <div className="text-xs font-bold text-foreground sm:text-sm">{v}</div>
                  <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{l}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-end text-xs font-medium text-primary">View capital account <ArrowRight className="ml-1 h-3 w-3" /></div>
          </Card>
        ))}
      </div>
    </AppShell>
  );
}
